import { ALL_TIERS, TRIAL_AI_REQUESTS, TRIAL_ONCE_RULE, type Tier, type TierInfo, type TierLimits } from '@shared/constants/tiers'
import type { SubscriptionLimitKey, SubscriptionSummary, TierChangePreview } from './types'

/**
 * Тарифы для экранов подписки (S19). Цифры - из каталога
 * packages/shared/src/constants/tiers.ts, он сверяется с сервером тестом
 * tiers.sync.test.ts. Старый TIER_INFO из types/organization.ts устарел.
 */

export const PLAN_ORDER: Tier[] = ['free', 'standard', 'pro', 'team', 'club', 'pro_team']

export { TRIAL_AI_REQUESTS, TRIAL_ONCE_RULE }

export function plans(): TierInfo[] {
  return PLAN_ORDER.map((id) => ALL_TIERS[id])
}

export function planOf(id: string): TierInfo | undefined {
  return PLAN_ORDER.includes(id as Tier) ? ALL_TIERS[id as Tier] : undefined
}

/** «PRO TEAM», а для незнакомого тарифа - его код заглавными. */
export function tierName(id: string): string {
  return planOf(id)?.name ?? id.toUpperCase().replace(/_/g, ' ')
}

/** Деньги как в прежнем интерфейсе: «4 990 ₽»; копейки - только если есть. */
export function rub(amount: number): string {
  return `${amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`
}

/** Цена со скидкой промокода - тем же правилом, что на сервере (applyPromoDiscount): не меньше 1 ₽. */
export function discounted(amount: number, percent: number): number {
  if (!percent || percent <= 0) return amount
  const v = amount * (1 - percent / 100)
  return v < 1 ? 1 : Math.round(v * 100) / 100
}

/** «1 день», «3 дня», «14 дней». */
export function daysLabel(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} день`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} дня`
  return `${n} дней`
}

export const LIMIT_LABEL: Record<SubscriptionLimitKey, string> = {
  members: 'Участники клуба',
  directions: 'Направления',
  athletes: 'Спортсмены',
  groups: 'Группы',
  coaches: 'Тренеры',
  ai: 'Запросы Штаба в месяц',
}

export const LIMIT_ORDER: SubscriptionLimitKey[] = ['members', 'directions', 'athletes', 'groups', 'coaches', 'ai']

const LIMIT_FIELD: Record<SubscriptionLimitKey, keyof TierLimits> = {
  members: 'members',
  directions: 'directions',
  athletes: 'athletes',
  groups: 'groups',
  coaches: 'coaches',
  ai: 'aiRequests',
}

/** Лимит тарифа по ключу экрана; -1 - без ограничения. */
export function planLimit(plan: TierInfo, key: SubscriptionLimitKey): number {
  return plan.limits[LIMIT_FIELD[key]]
}

export function limitText(max: number): string {
  return max === -1 ? 'без ограничения' : String(max)
}

export const STATE_LABEL: Record<SubscriptionSummary['state'], string> = {
  free: 'Бесплатный',
  trial: 'Пробный период',
  active: 'Активна',
  expired: 'Срок истёк',
  cancelled: 'Отменена',
}

/**
 * Что упрётся в лимит нового тарифа: уже сейчас больше, чем он позволяет.
 * Запросы Штаба сравниваются с лимитом месяца: в пробном периоде он меньше.
 */
export function overLimits(
  sub: SubscriptionSummary,
  plan: TierInfo,
  preview?: Pick<TierChangePreview, 'kind'>,
): { key: SubscriptionLimitKey; used: number; max: number }[] {
  const out: { key: SubscriptionLimitKey; used: number; max: number }[] = []
  for (const row of sub.limits) {
    let max = planLimit(plan, row.key)
    if (row.key === 'ai' && preview?.kind === 'trial' && max !== -1) max = Math.min(max, TRIAL_AI_REQUESTS)
    if (max !== -1 && row.used > max) out.push({ key: row.key, used: row.used, max })
  }
  return out
}

// ---------------------------------------------------------------------------
// Правила пробного периода и оплаты - тексты одним местом. Те же правила на
// сервере: CalculateTierChange (internal/domain/organization/tier_change.go).
// ---------------------------------------------------------------------------

/** Пересчёт при оплате: оплаченные дни переносятся, дни пробного периода - нет. */
const PAID_DAYS_RULE =
  '30 дней нового тарифа, а неиспользованные оплаченные дни текущего пересчитываются в дни нового. Дни пробного периода не пересчитываются.'

/** Вступление экрана тарифов: как начнётся платный тариф для этого клуба. */
export function plansRulesText(trialUsed: boolean): string {
  if (trialUsed) {
    return `Цена - за 30 дней. ${TRIAL_ONCE_RULE} У клуба он уже был, поэтому платный тариф - только с оплатой картой: ${PAID_DAYS_RULE}`
  }
  return (
    `Цена - за 30 дней. ${TRIAL_ONCE_RULE} Переход с FREE или после окончания подписки начинается с него, без оплаты. ` +
    `Продление тарифа, срок которого закончился, - платное. Остальные переходы оплачиваются картой: ${PAID_DAYS_RULE}`
  )
}

/**
 * Начнётся ли переход на тариф с пробного периода - то же правило, что на
 * сервере: клуб его ещё не получал, сейчас FREE или подписка закончилась, и
 * это не продление тарифа, срок которого закончился.
 */
export function trialStartsOn(sub: Pick<SubscriptionSummary, 'state' | 'paidTier' | 'trialUsed'>, plan: TierInfo): boolean {
  if (sub.trialUsed || plan.trialDays <= 0) return false
  if (sub.state === 'free') return true
  return (sub.state === 'expired' || sub.state === 'cancelled') && plan.id !== sub.paidTier
}

/** Что будет после пробного периода. */
export const TRIAL_AFTER_TEXT = `Если не оплатить до конца срока, клуб вернётся к лимитам FREE, данные останутся. ${TRIAL_ONCE_RULE} Потом платный тариф - только с оплатой`

/** Оплата во время пробного периода: оплаченные дни - с момента оплаты. */
export function trialLostText(days: number, currentTier: string): string {
  return `Пробный период ${tierName(currentTier)} закончится при оплате: 30 оплаченных дней начнутся сразу, оставшиеся ${daysLabel(days)} пробного периода к ним не добавляются`
}
